import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const DwellieDnaPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkDnaQuizStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setIsAuthenticated(false);
        setShowPrompt(false);
        return;
      }
      
      setIsAuthenticated(true);

      const dismissedAt = localStorage.getItem(`dna-quiz-dismissed-${session.user.id}`);
      if (dismissedAt) {
        const dismissedTime = new Date(dismissedAt).getTime();
        const now = new Date().getTime();
        if (now - dismissedTime < 24 * 60 * 60 * 1000) {
          return;
        }
      }

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("created_at, onboarding_completed, dwellie_dna_completed")
        .eq("user_id", session.user.id)
        .maybeSingle();
      
      if (profileError) {
        logger.error("Error fetching profile for Dwellie DNA prompt:", profileError);
      }

      if (!profile) return;

      // Don't show prompt if onboarding is not completed or quiz already completed
      if (!profile.onboarding_completed || profile.dwellie_dna_completed) return;

      const createdAt = new Date(profile.created_at).getTime();
      const now = new Date().getTime();
      const threeHours = 3 * 60 * 60 * 1000;

      if (now - createdAt >= threeHours) {
        setShowPrompt(true);
      }
    };

    checkDnaQuizStatus();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        checkDnaQuizStatus();
      } else {
        setIsAuthenticated(false);
        setShowPrompt(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleTakeQuiz = () => {
    setShowPrompt(false);
    navigate("/dwellie-dna-quiz");
  };

  const handleDismiss = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      localStorage.setItem(
        `dna-quiz-dismissed-${session.user.id}`,
        new Date().toISOString()
      );
    }
    setShowPrompt(false);
  };

  if (!isAuthenticated) return null;

  return (
    <AlertDialog open={showPrompt} onOpenChange={setShowPrompt}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>🧬 Discover Your Dwellie DNA</AlertDialogTitle>
          <AlertDialogDescription>
            Finding the right people to live with isn't just about rent or location — it's about chemistry. The Dwellie DNA Quiz helps you uncover your living "type" — how you communicate, what rhythms you thrive in, and the kind of house harmony you bring.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleDismiss}>
            Maybe Later
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleTakeQuiz}>
            Take Quiz Now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
